import { Draggable } from '@hello-pangea/dnd';
import { Snowflake, MessageSquare } from 'lucide-react';
import type { Operacao } from '../types';
import { SUB_STATUS_CONFIG_POR_ENUM, ESTEIRA_COLOR, ORIGEM_STYLE } from '../types';

interface KanbanCardProps {
  operacao: Operacao;
  index: number;
  onClick: (op: Operacao) => void;
}

function formatMoeda(valor: number | null | undefined): string {
  if (valor == null || valor === 0) return '-';
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

// SUB_STATUS_CONFIG_POR_ENUM usado aqui apenas como cache de display no board
// principal. A fonte de verdade é a tabela etapas_sub_esteira (consumida pelo
// SubBoard via API). Chaveado pelo enum bruto (esteiraAtualEnum), não pelo
// bucket visual - o bucket "Crédito" (esteira=1) agrupa ENTRADA e CREDITO,
// cujas listas de sub-status são diferentes.
function faseLabel(op: Operacao): string {
  return SUB_STATUS_CONFIG_POR_ENUM[op.esteiraAtualEnum]?.[op.subStatus - 1]?.label ?? '-';
}

function localidade(op: Operacao): string | null {
  if (op.imovelDetalhe?.cidade) {
    return op.imovelDetalhe.estado
      ? `${op.imovelDetalhe.cidade} / ${op.imovelDetalhe.estado}`
      : op.imovelDetalhe.cidade;
  }
  if (op.enderecoImovel) {
    const match = op.enderecoImovel.match(/([A-Za-zÀ-ú ]+\/[A-Z]{2})\s*$/);
    if (match) return match[1].trim();
  }
  return null;
}

export default function KanbanCard({ operacao: op, index, onClick }: KanbanCardProps) {
  const congelada = op.status === 'CONGELADA';
  const origem = ORIGEM_STYLE[op.origem] ?? { badgeBg: '#F1F3F5', badgeTxt: '#5F6470', stripe: '#9AA0AC' };
  const esteiraColor = ESTEIRA_COLOR[op.esteira];
  const fase = faseLabel(op);
  const local = localidade(op);
  const valor = formatMoeda(op.valorFinanciamentoDesejado);
  const nomeComprador = op.compradores[0]?.nome ?? '-';
  const coPartes = op.compradores.length - 1 + op.vendedores.length;

  const aging =
    op.diasNaEsteira > 10 ? 'bg-red-50 text-red-600' :
    op.diasNaEsteira > 5  ? 'bg-amber-50 text-amber-700' :
                             'bg-[#EAECF2] text-[#5F6470]';

  // Pré-CCV (SPEC 3): estado derivado, sem migration - ccvDataAssinatura null = pré-CCV.
  // Só faz sentido no universo 5A (exceção manual "Buscar no iSolve").
  const preCcv = !op.ccvDataAssinatura && op.origem === '5A';

  return (
    <Draggable draggableId={op.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...(provided.draggableProps.style as React.CSSProperties),
            opacity: congelada ? 0.6 : 1,
          }}
          {...provided.dragHandleProps}
          onClick={() => onClick(op)}
          className={`
            relative bg-white rounded-[8px] border border-[#E2E6ED] overflow-hidden
            cursor-pointer select-none transition-all duration-150
            hover:border-[#C5CDD8] hover:bg-[#FAFBFC]
            ${snapshot.isDragging ? 'shadow-lg rotate-[0.5deg]' : 'shadow-sm'}
          `}
        >
          {/* Faixa 3px por origem */}
          <div
            className="absolute left-0 top-0 bottom-0 w-[3px]"
            style={{ backgroundColor: origem.stripe }}
          />

          <div className="pl-3 pr-3 pt-2.5 pb-2.5">
            {/* Linha 1: número do processo + farol de eventos (SPEC S1) + badge origem */}
            <div className="flex items-center justify-between gap-1.5 mb-1.5">
              <span className="text-[10px] text-[#9AA0AC] font-mono tabular-nums truncate">
                {op.numeroProcesso}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                {!!op.eventosNaoVistos && op.eventosNaoVistos > 0 && (
                  <span
                    className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-[2px] rounded-full tabular-nums ${
                      op.temAcionavelNaoVisto ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }`}
                    title={`${op.eventosNaoVistos} evento(s) novo(s) desde a última vez que esta operação foi aberta`}
                  >
                    <span
                      className="w-[6px] h-[6px] rounded-full shrink-0"
                      style={{ backgroundColor: op.temAcionavelNaoVisto ? '#B45309' : '#1D4ED8' }}
                    />
                    {op.eventosNaoVistos}
                  </span>
                )}
                <span
                  className="text-[10px] font-medium px-1.5 py-[2px] rounded"
                  style={{ backgroundColor: origem.badgeBg, color: origem.badgeTxt }}
                >
                  {op.origem}
                </span>
              </div>
            </div>

            {/* Linha 2: nome do comprador */}
            <p className="text-[13px] font-medium text-[#1A1D23] leading-snug truncate">
              {nomeComprador}
              {coPartes > 0 && (
                <span className="text-[11px] font-normal text-[#9AA0AC] ml-1">
                  +{coPartes}
                </span>
              )}
            </p>

            {/* Linha 3: cidade/UF */}
            {local && (
              <p className="text-[11px] text-[#5F6470] mt-0.5 truncate">{local}</p>
            )}

            {/* Linha 4: valor do imóvel */}
            <p className="text-[11px] text-[#5F6470] tabular-nums mt-0.5">
              {valor}
            </p>

            {/* Divisor */}
            <div className="my-2 border-t border-[#EAECF2]" />

            {/* Rodapé: ponto cor-esteira + fase + dias */}
            <div className="flex items-center justify-between gap-1.5">
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  className="shrink-0 w-[7px] h-[7px] rounded-full"
                  style={{ backgroundColor: esteiraColor }}
                />
                <span className="text-[11px] text-[#5F6470] truncate">{fase}</span>
              </div>
              <span className={`shrink-0 text-[10px] px-1.5 py-[2px] rounded tabular-nums font-medium ${aging}`}>
                {op.diasNaEsteira}d
              </span>
            </div>

            {/* Badges extras - msgs (compactos, abaixo do rodapé se existirem).
                BUG S1 (03/07/2026, confirmado live): o badge de documentosPendentes
                (📄, contagem de TODOS os Documento da operação, não só pendentes)
                usava cor quente (orange) parecida com o farol de eventos (âmbar) e
                sem rótulo - operador confundiu doc-count com evento (ex.: OTM-2026-0895
                mostrava "14" = contagem de documentos, com 0 eventos reais). Decisão
                do Emerson: REMOVER do card no R1 - anti-ruído, card mostra só o farol
                de eventos; contagem de documentos já vive na aba Docs do modal. */}
            {(op.mensagensNaoLidas > 0 || congelada || preCcv || (op.clienteConduz && op.esteira === 3)) && (
              <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                {preCcv && (
                  <span
                    className="text-[9px] bg-slate-100 text-slate-500 px-1.5 py-[1px] rounded"
                    title="Importado antes da assinatura do CCV (exceção manual)"
                  >
                    pré-CCV
                  </span>
                )}
                {congelada && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] bg-[#EAECF2] text-[#5F6470] px-1.5 py-[1px] rounded"><Snowflake size={9} /> pausada</span>
                )}
                {op.clienteConduz && op.esteira === 3 && (
                  <span className="text-[9px] bg-teal-50 text-teal-700 px-1.5 py-[1px] rounded">CC</span>
                )}
                {op.mensagensNaoLidas > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-[1px] rounded">
                    <MessageSquare size={9} /> {op.mensagensNaoLidas}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}
